import { useEffect } from "react";
import { api } from "../../lib/api";

// Strict allow-list regexes. Analytics IDs that fail these are never injected —
// this prevents template-string injection into inline scripts, even if an
// attacker briefly compromises the admin settings collection.
const GA4_ID_RE = /^G-[A-Z0-9]{4,12}$/;
const ADSENSE_ID_RE = /^ca-pub-\d{10,20}$/;
const FB_PIXEL_ID_RE = /^\d{10,20}$/;
const GSV_RE = /^[A-Za-z0-9_-]{20,120}$/;

const setScript = (id, code) => {
  // Inline scripts injected via textContent are treated as script bodies, but
  // the content never gets HTML-parsed — so any `<`/`&` cannot break out.
  const s = document.createElement("script");
  s.id = id;
  s.type = "text/javascript";
  s.textContent = code;
  document.head.appendChild(s);
};

// Injects GA4, AdSense, and Facebook Pixel scripts if IDs are configured via admin settings.
export default function AnalyticsInjector() {
  useEffect(() => {
    let mounted = true;
    api.get("/settings/public")
      .then(({ data }) => {
        if (!mounted) return;
        const { ga4_id, adsense_id, fb_pixel_id, google_site_verification } = data || {};

        if (GSV_RE.test(google_site_verification || "") && !document.getElementById("gsv-meta")) {
          const m = document.createElement("meta");
          m.id = "gsv-meta";
          m.name = "google-site-verification";
          m.content = google_site_verification;
          document.head.appendChild(m);
        }

        if (GA4_ID_RE.test(ga4_id || "") && !document.getElementById("ga4-script")) {
          const s1 = document.createElement("script");
          s1.id = "ga4-script";
          s1.async = true;
          s1.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga4_id)}`;
          document.head.appendChild(s1);
          // Safe: ga4_id is regex-validated to [A-Z0-9-]+ so cannot break the string literal.
          setScript(
            "ga4-inline",
            `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${ga4_id}');`,
          );
        }

        if (ADSENSE_ID_RE.test(adsense_id || "") && !document.getElementById("adsense-script")) {
          const s = document.createElement("script");
          s.id = "adsense-script";
          s.async = true;
          s.crossOrigin = "anonymous";
          s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(adsense_id)}`;
          document.head.appendChild(s);
        }

        if (FB_PIXEL_ID_RE.test(fb_pixel_id || "") && !document.getElementById("fb-pixel-inline")) {
          // Safe: fb_pixel_id is regex-validated to be digits only.
          setScript(
            "fb-pixel-inline",
            `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${fb_pixel_id}');fbq('track','PageView');`,
          );
        }
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);
  return null;
}
